import { getRoute } from 'helpers/route.helper/route.helper'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Tabs } from 'ui/atoms'
import Title from 'ui/molecules/Title/Title'
import { getComponentTitle, registerDefaultProps, registerUiConfigComponent } from 'ui/uiConfig/factory'
import UiConfigComponent from 'ui/uiConfig/UiConfigComponent'
import type { CardWidgetProps, CardWidgetTab, CardWidgetTabItem } from './CardWidget.types'

// Re-export for backward compatibility
export type { CardWidgetTab, CardWidgetTabItem }

const defaultProps: Pick<CardWidgetProps, 'use_default' | 'card' | 'tabs'> = {
  use_default: false,
  card: true,
  tabs: [
    {
      title: 'Tab 1',
      children: [
        {
          id: 0,
          override_alias: {},
          override_props: {},
        },
      ],
    },
  ],
}
registerUiConfigComponent('card_widget', CardWidget)
registerDefaultProps('card_widget', defaultProps)

function generateChildren(
  children: UiConfigId | UiConfigId[] | CardWidgetTabItem[],
  props: Partial<CardWidgetProps>
): ReactElement | ReactElement[] {
  delete props.id
  delete props.uid
  delete props.key

  if (typeof children === 'number') {
    return <UiConfigComponent {...props} id={children} />
  }

  return children.map((child, index) => {
    if (typeof child === 'number') {
      return <UiConfigComponent {...props} key={index} id={child} />
    }

    const overrideAlias = {
      ...(props.overrideAlias || {}),
      ...(child.override_alias || {}),
    }

    return (
      <UiConfigComponent
        {...props}
        key={index}
        id={child.id}
        overrideAlias={overrideAlias}
        overrideProps={child.override_props}
      />
    )
  })
}

export default function CardWidget({
  component,
  children,
  tabs = [],
  card = true,
  ...props
}: CardWidgetProps): ReactElement {
  const { t } = useTranslation()
  const route = getRoute()

  const title = t(`${getComponentTitle(component, true)}`)
  const tabItems = tabs.map((tab) => ({
    title: tab.title,
    children: generateChildren(tab.children, props),
  }))
  const body = (
    <>
      {title && <Title title={title} />}
      {tabItems.length ? (
        <Tabs uniqueStorageKey={`Cardwidget_${route}_${title}_Tabs`} items={tabItems} />
      ) : (
        <>{children}</>
      )}
    </>
  )

  if (!card) {
    return <>{body}</>
  }

  return <Card>{body}</Card>
}
